import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Inject,
  Optional,
  signal,
  ViewChild,
} from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { MatInputModule } from '@angular/material/input';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { catchError, of, takeUntil } from 'rxjs';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import { FormlyDataCyDirective } from '../../directives/formly-data-cy.directive';
import { FieldLabelRowComponent } from './field-label-row.component';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { EntriesService } from '../../../features/admin/modules/features/entries/entries.service';
import { UtilityService } from '../../services/utility.service';
import { UserStore } from 'app/core/services/user.store';
import { TitleService } from '../../services/title.service';
import type { Entry } from '../../../features/admin/modules/modules.model';
import { EntriesLoadErrorAlertComponent } from '../entries-load-error-alert/entries-load-error-alert.component';
import { entryDisplayName, LinkageEntriesSearch } from './services/linkage-entries-search';

interface LinkageTag {
  linkageEntryId: string;
  entryId: string;
  label: string;
}

class DialogData {}

@Component({
  selector: 'formly-linkage-tags',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatInputModule,
    ReactiveFormsModule,
    FormlyModule,
    MatChipGrid,
    MatChipInput,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatAutocomplete,
    MatAutocompleteTrigger,
    MatOption,
    MatProgressSpinner,
    FuseAlertComponent,
    FormlyDataCyDirective,
    FieldLabelRowComponent,
    EntriesLoadErrorAlertComponent,
  ],
  template: `
    <div>
      <mat-form-field class="tags-list w-full" appearance="outline" floatLabel="always">
        <mat-label>
          <app-field-label-row [label]="props.label" [required]="props.required" [info]="props.info"></app-field-label-row>
        </mat-label>
        <!-- Chips INSIDE the grid: outside it, the grid (with the input) becomes its own
             block and the caret jumps to the next line even when the current one has room. -->
        <mat-chip-grid #chipGrid aria-label="Koppelingen invoerveld">
          @for (tag of tags(); track tag.linkageEntryId) {
            <mat-chip-row [disabled]="!canMutate()" class="mr-1 dd-tag-chip" (removed)="removeTag(tag)">
              {{ tag.label }}
              @if (canMutate()) {
                <button matChipRemove [attr.aria-label]="'verwijder ' + tag.label">
                  <mat-icon svgIcon="close"></mat-icon>
                </button>
              }
            </mat-chip-row>
          }
          <input
            matInput
            type="text"
            class="pl-2"
            #searchInput
            [formControl]="searchControl"
            [formlyDataCy]="field"
            [placeholder]="!linkedEntryId() || !canMutate() ? '' : 'Zoeken...'"
            [readonly]="!canMutate()"
            [matChipInputFor]="chipGrid"
            [matAutocomplete]="auto"
            (focus)="onSearchFocus()" />
          @if (isSearching()) {
            <mat-spinner class="absolute right-8" diameter="24"></mat-spinner>
          }
          <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selectEntry($event)">
            @for (option of filteredSearchResults(); track option.value.id) {
              <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-autocomplete>
        </mat-chip-grid>
        @if (field.props.description) {
          <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
        }
      </mat-form-field>
      <!-- A 403 on the search must not read as "no results" — see entries-load-error.ts. -->
      @if (loadError()) {
        <app-entries-load-error-alert class="my-2" [error]="loadError()!" [label]="props.label" dataCyPrefix="linkage-tags" />
      }
      @if (!linkedEntryId()) {
        <fuse-alert class="my-2" [type]="'warning'">
          Je kan pas koppelingen toevoegen als je huidige item hebt opgeslagen.
        </fuse-alert>
      }
    </div>
  `,
})
export class FormlyLinkageTagsComponent extends FieldType implements AfterViewInit {
  readonly #modulesStore = inject(ModulesStore);
  readonly #entriesService = inject(EntriesService);
  readonly #userStore = inject(UserStore);
  readonly #titleService = inject(TitleService);
  readonly #destroy$ = injectDestroy();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  route = inject(ActivatedRoute);
  paramsParent;
  params = toSignal(this.route.paramMap);

  searchControl = new FormControl();

  linkageEntries = signal<Entry[]>([]);
  readonly #pendingTags = signal<LinkageTag[]>([]);

  readonly #search = new LinkageEntriesSearch({
    entriesService: this.#entriesService,
    destroy$: this.#destroy$,
    namespace: 'LinkageTags',
    searchModule: () => this.searchModule(),
  });
  isSearching = this.#search.isSearching;
  searchResults = this.#search.searchResults;
  loadError = this.#search.loadError;

  currentModule = computed(() => {
    if (this.paramsParent() === undefined) {
      return this.data['entity'];
    }
    if (this.paramsParent()?.get('name') === 'bedrijfsinstellingen') {
      return this.#modulesStore.enabledModules().find(module => module.slug === 'companies');
    }
    if (this.paramsParent()?.get('name') === 'profiel') {
      return this.#modulesStore.enabledModules().find(module => module.slug === 'users');
    }
    return this.#modulesStore.enabledModules().find(module => module.slug === this.paramsParent()?.get('name'));
  });

  currentEntry = computed(() => {
    if (this.params()?.get('id') === undefined) {
      return this.data['model'];
    }
    if (this.options.formState?.entry != null) {
      return this.options.formState.entry;
    }
    return this.#modulesStore.entries()[this.currentModule()?.id]?.find(entry => {
      return entry.id === this.params()?.get('id');
    });
  });

  linkedEntryId = computed(() => {
    if (this.options.formState?.entry?.id != null) {
      return this.options.formState.entry.id;
    } else if (this.#titleService.getTitle().toLowerCase() === 'profiel') {
      return this.#userStore.currentUserId();
    } else if (this.data != undefined && this.data['model']?.id) {
      return this.data['model']?.id;
    } else {
      return this.params()?.get('id');
    }
  });

  linkedModule = computed(() => {
    if (!this.props.linkedModule) return undefined;
    return this.#modulesStore.enabledModules().find(module => module.code === this.props.linkedModule);
  });

  searchModule = computed(() => {
    if (!this.props.searchModuleCode) return undefined;
    return this.#modulesStore.enabledModules().find(module => module.code === this.props.searchModuleCode);
  });

  tags = computed<LinkageTag[]>(() => {
    const fromEntries = this.linkageEntries()
      .map(entry => this.extractTag(entry))
      .filter((tag): tag is LinkageTag => tag != null);
    const pending = this.#pendingTags();
    // Pending tags that have not yet arrived via entries
    const entryIds = new Set(fromEntries.map(t => t.entryId));
    const uniquePending = pending.filter(t => !entryIds.has(t.entryId));
    return [...fromEntries, ...uniquePending];
  });

  filteredSearchResults = computed(() => {
    const linkedEntryIds = new Set(this.tags().map(tag => tag.entryId));
    return this.searchResults().filter(option => !linkedEntryIds.has(option.value.id));
  });

  constructor(@Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData) {
    super();
    if (this.route?.parent?.paramMap) {
      this.paramsParent = toSignal(this.route?.parent?.paramMap);
    }
  }

  ngAfterViewInit() {
    requestAnimationFrame(() => {
      this.loadLinkedEntries();
      this.#search.connect(this.searchControl);
    });
  }

  onSearchFocus() {
    if (!this.canMutate()) return;
    if (!this.searchControl.value && this.searchModule()) {
      this.#search.searchOnFocus();
    }
  }

  selectEntry(event: { option: { value: Entry } }) {
    if (!this.canMutate()) return;
    const selectedEntry = event.option.value;
    this.createLinkage(selectedEntry);

    if (this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    // clear() owns the whole reset. The autocomplete already wrote the selected Entry into
    // the control (emitting) before this handler ran; that buffered emission is dropped by
    // the pipeline's control.value guard, so it cannot cancel a focus search started next.
    this.#search.clear();
  }

  removeTag(tag: LinkageTag) {
    if (!this.canMutate()) return;
    const linkageModule = this.linkedModule();
    if (!linkageModule?.id || !tag.linkageEntryId) return;

    this.#modulesStore.deleteEntry({
      moduleId: linkageModule.id,
      entryId: tag.linkageEntryId,
    });

    this.linkageEntries.update(entries => entries.filter(e => e.id !== tag.linkageEntryId));
  }

  isDisabled(): boolean {
    return !!this.formControl?.disabled || !!this.field.props.disabled || !!this.form?.disabled;
  }

  /**
   * Disabled only: read mode never reaches this component. `linkage-tags` is a wrappable type, so
   * `props.readonly` makes `ReadonlyWrapper` render a label/value row INSTEAD of the field —
   * Formly never builds the type component at all (readonly/readonly.wrapper.ts, #21239).
   */
  canMutate(): boolean {
    return !this.isDisabled();
  }

  private loadLinkedEntries() {
    const entryId = this.linkedEntryId();
    const linkageModule = this.linkedModule();
    if (!entryId || !linkageModule?.id) return;

    const fieldA = this.props.linkedFieldA;
    const fieldB = this.props.linkedFieldB;
    if (!fieldA || !fieldB) return;

    const filterA = `${UtilityService.dotPathToObjectPath(fieldA + '.id')} eq '${entryId}'`;
    const filterB = `${UtilityService.dotPathToObjectPath(fieldB + '.id')} eq '${entryId}'`;

    const moduleFilterId = this.getModuleFilterId() ?? this.searchModule()?.id;
    let filter: string;

    if (moduleFilterId) {
      const moduleIdPathA = UtilityService.dotPathToObjectPath(fieldA.split('.').slice(0, -1).join('.') + '.moduleId');
      const moduleIdPathB = UtilityService.dotPathToObjectPath(fieldB.split('.').slice(0, -1).join('.') + '.moduleId');
      filter = `(${filterA} and ${moduleIdPathB} eq '${moduleFilterId}') or (${filterB} and ${moduleIdPathA} eq '${moduleFilterId}')`;
    } else {
      filter = `${filterA} or ${filterB}`;
    }

    this.#entriesService
      .getEntries({
        moduleId: linkageModule.id,
        offset: 0,
        limit: 1000,
        filter,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe(entries => {
        this.linkageEntries.set(entries);
      });
  }

  private extractTag(linkageEntry: Entry): LinkageTag | undefined {
    const otherItem = this.getOtherSideItem(linkageEntry);
    const otherEntry = otherItem?.entry;
    if (!otherEntry?.id) return undefined;

    // Filter: only show tags where the other side belongs to this field's searchModule
    const searchModId = this.searchModule()?.id;
    if (searchModId && otherItem?.moduleId && otherItem.moduleId !== searchModId) {
      return undefined;
    }

    return {
      linkageEntryId: linkageEntry.id,
      entryId: otherEntry.id,
      label: otherEntry.selectedInput || 'Onbekend',
    };
  }

  private getOtherSideItem(
    linkageEntry: Entry
  ): { moduleId?: string; entry?: { id?: string; selectedInput?: string } } | undefined {
    if (!linkageEntry?.entry) return undefined;

    const fieldAParts = this.props.linkedFieldA?.split('.') ?? [];
    const fieldBParts = this.props.linkedFieldB?.split('.') ?? [];
    const itemAKey = fieldAParts[1];
    const itemBKey = fieldBParts[1];

    const itemAPath = fieldAParts.slice(1);
    const itemBPath = fieldBParts.slice(1);

    const entryA = this.getNestedValue(linkageEntry.entry, itemAPath);
    const entryB = this.getNestedValue(linkageEntry.entry, itemBPath);

    if (entryA?.id === this.linkedEntryId()) {
      return linkageEntry.entry[itemBKey];
    }
    return linkageEntry.entry[itemAKey];
  }

  private getNestedValue(obj: any, path: string[]): any {
    return path.reduce((current, key) => current?.[key], obj);
  }

  private getModuleFilterId(): string | undefined {
    const defaults = this.props.defaultValuesLinkedItems;
    if (!defaults?.length) return undefined;

    const moduleDefault = defaults.find(item => item.targetField?.endsWith('moduleId'));
    if (!moduleDefault) return undefined;

    switch (moduleDefault.basedOn) {
      case 'moduleId':
        return this.currentModule()?.id;
      case 'static':
        return moduleDefault.value;
      default:
        return undefined;
    }
  }

  private createLinkage(selectedEntry: Entry) {
    if (!this.canMutate()) return;
    const linkageModule = this.linkedModule();
    const searchMod = this.searchModule();
    const currentEntry = this.currentEntry();
    if (!linkageModule?.id || !currentEntry?.id || !searchMod) return;

    const fieldA = this.props.linkedFieldA;
    const fieldB = this.props.linkedFieldB;
    if (!fieldA || !fieldB) return;

    const selectedLabel = selectedEntry.selectedInput ?? entryDisplayName(selectedEntry, searchMod);

    // Optimistic: add tag immediately in the UI with a temporary ID
    const tempId = `temp-${Date.now()}`;
    this.linkageEntries.update(entries => [...entries, { id: tempId, entry: null } as Entry]);
    // Add a tag immediately via a separate signal so the UI responds instantly
    this.#pendingTags.update(tags => [
      ...tags,
      { linkageEntryId: tempId, entryId: selectedEntry.id, label: selectedLabel },
    ]);

    const newEntry: Entry = {
      id: undefined,
      entry: { archived: false },
    };

    // Side A: current entry
    UtilityService.setKeyValuePath(newEntry, fieldA, {
      id: currentEntry.id,
      selectedInput: currentEntry.selectedInput ?? this.getCurrentEntryDisplayName(),
    });
    const moduleIdPathA = fieldA.split('.').slice(0, -1).join('.') + '.moduleId';
    UtilityService.setKeyValuePath(newEntry, moduleIdPathA, this.currentModule()?.id);

    // Side B: selected entry
    UtilityService.setKeyValuePath(newEntry, fieldB, {
      id: selectedEntry.id,
      selectedInput: selectedLabel,
    });
    const moduleIdPathB = fieldB.split('.').slice(0, -1).join('.') + '.moduleId';
    UtilityService.setKeyValuePath(newEntry, moduleIdPathB, searchMod.id);

    // Apply additional default values
    const defaults = this.props.defaultValuesLinkedItems;
    if (defaults?.length) {
      for (const item of defaults) {
        if (item.targetField?.endsWith('moduleId')) continue;
        let value: any;
        switch (item.basedOn) {
          case 'moduleId':
            value = this.currentModule()?.id;
            break;
          case 'static':
            value = item.value;
            break;
          case 'entry':
            value = item.sourceField ? UtilityService.keyPathToValue(currentEntry, item.sourceField) : undefined;
            break;
        }
        if (value !== undefined) {
          UtilityService.setKeyValuePath(newEntry, item.targetField, value);
        }
      }
    }

    this.#entriesService
      .addEntry(linkageModule.id, newEntry.entry)
      .pipe(
        takeUntil(this.#destroy$),
        catchError(e => {
          console.error('Failed to create linkage:', e);
          // Rollback: remove the optimistically added tag
          this.#pendingTags.update(tags => tags.filter(t => t.linkageEntryId !== tempId));
          this.linkageEntries.update(entries => entries.filter(e => e.id !== tempId));
          return of(null);
        })
      )
      .subscribe(result => {
        if (result) {
          // Replace the temporary tag with real data
          this.#pendingTags.update(tags => tags.filter(t => t.linkageEntryId !== tempId));
          this.linkageEntries.update(entries => [...entries.filter(e => e.id !== tempId), result]);
        }
      });
  }

  private getCurrentEntryDisplayName(): string {
    const entry = this.currentEntry();
    // '' and no id-fallback: a GUID is not a usable label for the current-entry side.
    return entry ? entryDisplayName(entry, this.currentModule(), { fallback: '', idFallback: false }) : '';
  }
}
